import {Component, Element, h, Listen, Prop} from '@stencil/core';
import {getAnalyticsCat, getSiteSearchCategory, pushToDataLayer} from '../util';
import type {SearchResultsPresentationEvent} from '../../result-page/search-tab/SearchResultsPresentationEvent';
import type {FacetChangedEvent} from '../../result-page/search-tab/FacetChangedEvent';
import type {SearchResultClickEvent} from '../../result-page/search-tab/SearchResultClickEvent';
import type {DocumentDownloadEvent} from '../../result-page/result-cards/utils/DocumentDownloadEvent';
import type {AddToCartEvent} from '../../result-page/result-cards/public/AddToCartEvent';
import type {ViewItemListEvent} from '../../result-page/search-tab/ViewItemListEvent';
import {ResultCardType} from '../../result-page/new-result-cards/public/product-card/helpers/helpers';
import type {TabNavbarClicked} from '../../result-page/tabs-header/TabNavbarClicked';
import type {IdentifyActiveTabEvent} from '../../result-page/search/TrackAnalyticsEvent';
import type {RedirectionBannerEvent} from '../../widgets/redirection-banner/RedirectionBannerEvent';
import type {UIaaSCompareBarEvents} from '../../compare/compare-bar/uiaas-compare-bar-events';
import type {UIaaSCheckboxEvents} from '../../compare/checkbox/uiaas-checkbox-events';
import type {
  ProductRecommendationWidgetEvent
} from '../../widgets/product-recommendation/ProductRecommendationWidgetEvent';
import {
  MobileFilteringCategoryType
} from '../../design-system/mobile-filters/models/mobile-filtering-category-type.enum';
import {MobileFilteringActionType} from '../../design-system/mobile-filters/models/mobile-filtering-action-type.enum';
import {SidePanelActionType} from '../../result-page/product-side-panel/models/side-panel-action-type.enum';
import {CompareBarAreaType} from '../../compare/compare-bar/models/compare-bar-area-type.enum';
import {Strategies, StrategiesFactory} from '../../strategies/Strategies';
import {TransformEventsNamesStrategyService} from '../../strategies/TransformEventsNamesStrategyService';
import {TRANSFORM_EVENTS_NAMES} from '../../strategies/utils/variables';

import {FILTERS_ID_SEPARATOR} from '../../../constants/filterConstants';
import {
  EventLabelSeparatorConstant, SEARCH_BAR_EVENT_TARET,
  VOICE_SEARCH_EVENT_TARGET
} from '../constants/event-constants';
import type {CtaWidgetClickEvent} from '../../widgets/cta-widget-v2/cta-widget-v2';
import type {ItemClickedEvent} from '../../../types/item-clicked.type';
import type {CtaClickEvent} from '../../../types/cta-click.type';


@Component({
  tag: 'uiaas-search-analytics-reporting-se',
})
export class UiaasSearchAnalyticsReportingSeComponent {
  @Prop() readonly language?: String;
  @Prop() readonly businessUnit?: String;
  @Prop() readonly articleTitle?: String;
  @Prop() readonly digitalPlatform = '';
  @Prop() readonly digitalPlatformCountry?: String;
  @Prop() readonly env?: String;
  @Prop() readonly fromWebView?: Boolean;
  @Prop() readonly pageTopCategory?: String;
  @Prop() readonly pageCategory?: String;
  @Prop() readonly template?: String;
  @Prop() readonly pageTitle?: String;
  @Element() el;

  private activeTabName = '';
  private EVENT_LABEL_SEPARATOR = '###';

  private _strategies: Strategies;
  private transformEventsNames: TransformEventsNamesStrategyService;

  private prepareEventData = (data) => this.transformEventsNames?.transformEventsNames(data);

  get strategies(): Strategies {
      return this._strategies;
  }

  set strategies(value: Strategies) {
      this._strategies = value;
  }

  //Technical events broadcasted by UIAAS,
  // provided for integration with other components

  componentWillLoad() {
      this.strategies = StrategiesFactory.createStrategy(this.el);
      this.transformEventsNames = this.strategies.for(TRANSFORM_EVENTS_NAMES);
  }
  @Listen('search:SearchResultsPresentationEvent')
  searchResultsPresentationEventHandler(
    e: SearchResultsPresentationEvent & CustomEvent
  ) {
      const {event, appName, kw55, guidedSearch, refinedSearch, siteSearchResults, tabName, suggestion, sort} = e.detail;
      const details: SearchResultsPresentationEvent = {
        event,
        appName,
        kw55,
        guidedSearch,
        refinedSearch,
        siteSearchResults
      };
      const category = tabName?.substring(tabName?.indexOf('/') + 1);
      const isSearchSourceVoiceSearch = sessionStorage.getItem('isSearchSourceVoiceSearch') === 'true';

      const label = [
        ['tabName', tabName],
        ['hits', siteSearchResults],
        ['misspelling', !!suggestion],
        ['misspelledKeyword', suggestion ? kw55 : null],
        ['sort', sort ? sort : 'relevancy'],
        ['category', category],
        ['source', isSearchSourceVoiceSearch ? VOICE_SEARCH_EVENT_TARGET : SEARCH_BAR_EVENT_TARET],
      ].filter(el => el[1] !== null).map(el => el.join(':')).join(this.EVENT_LABEL_SEPARATOR);

      details.kw55 = suggestion || kw55;
      details.site_search_category = isSearchSourceVoiceSearch ? VOICE_SEARCH_EVENT_TARGET : getAnalyticsCat(appName);
      details.label = label;
      pushToDataLayer(this.prepareEventData(details));
  }

  @Listen('search:FacetClickEvent')
  facetClickEventHandler(e: FacetChangedEvent & CustomEvent) {
      const {
        tabName,
        filterAction,
        filterCategory,
        filterName,
        event,
        filterCount,
        clickedArea,
        filterSearchInputValue
      } = e.detail;
      const details: FacetChangedEvent = {event, filterAction, filterCategory, filterName, filterCount};
      const clickedAreaValue = clickedArea || 'checkbox';
      details.site_search_label = [
        ['tabName', tabName],
        ['clickedArea', clickedAreaValue], ['filterSearchInput', filterSearchInputValue || '']].filter(el => el[1] !== null).map(el => el.join(':')).join(this.EVENT_LABEL_SEPARATOR);
      pushToDataLayer(this.prepareEventData(details));
  }

  @Listen('search:SearchResultClickEvent')
  searchResultClickEventHandler(event: SearchResultClickEvent & CustomEvent) {
      if (event.detail.clickedArea === CompareBarAreaType.CompareNowButton) {
        return;
      }

      const isSearchSourceVoiceSearch = sessionStorage.getItem('isSearchSourceVoiceSearch') === 'true';
      const label = event.detail.clickedArea === CompareBarAreaType.CompareNowButton ?
        [
          'clickedArea:' + event.detail.clickedArea,
          'clickType:' + event.detail.clickType,
        ].join(this.EVENT_LABEL_SEPARATOR)
        : [
          'clickedArea:' + event.detail.clickedArea,
          'tab:' + event.detail.tabName,
          'position:' + event.detail.searchResultPosition,
          'clickType:' + event.detail.clickType,
          'commStatus:' + event.detail.commStatus,
          'source:' + (isSearchSourceVoiceSearch ? VOICE_SEARCH_EVENT_TARGET : event.detail.isPromotedWidget ? 'promoted-products-widget' : SEARCH_BAR_EVENT_TARET),
          event.detail?.circularNature && `tag:${event.detail.circularNature}`
        ].filter(Boolean).join(this.EVENT_LABEL_SEPARATOR);

      if (event.detail.clickedArea !== ResultCardType.Dropdown) {
        pushToDataLayer(
          this.prepareEventData({
            event: event.detail.event,
            searchPagePosition: event.detail.searchPagePosition,
            searchResultPosition: event.detail.searchResultPosition,
            site_search_category: event.detail.site_search_category,
            siteSearchKeyword: event.detail.siteSearchKeyword,
            rightButton: event.detail.rightButton,
            is_filter_use: event.detail.is_filter_use,
            destinationURL: event.detail.openUrl,
            label: label,
            search_location: event.detail.search_location ? event.detail.search_location : null,
          }));
      }

      if (event.detail.resutCardType === ResultCardType.Product) {
        pushToDataLayer(this.prepareEventData({ecommerce: null})); // Clear the previous ecommerce object.
        pushToDataLayer(
          this.prepareEventData({
            event: 'select_item',
            ecommerce: {
              currency: event.detail?.currency || '',
              value: event.detail?.totalPrice || '',

              items: [
                {
                  item_id: event.detail.id,
                  item_name: event.detail.title,
                  item_brand: event.detail?.brand || '',
                  item_list_name: 'product_list',
                  item_list_id: event.detail?.listId || '',
                  item_category: event.detail?.category || '',
                  item_variant: event.detail?.variant || '',
                  price: event.detail?.price || '',
                  quantity: event.detail?.quantity || '',
                },
              ],
            },
          }));
      }
  }

  @Listen('search:DocumentDownloadEvent')
  documentDownloadEventHandler(event: DocumentDownloadEvent & CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: event.detail.event,
        documentId: event.detail.documentId,
        documentName: event.detail.documentName,
        documentType: event.detail.documentType,
      }));
  }

  @Listen('search:AddToCartEvent')
  addToCartEventHandler(event: AddToCartEvent & CustomEvent) {
      pushToDataLayer(this.prepareEventData({ecommerce: null}));
      pushToDataLayer(this.prepareEventData({
        event: event.detail.event,
        addToCartLocation: event.detail.location,
        currencyCode: event.detail.currencyCode,
        distributor: event.detail.distributor,
        products: event.detail.products,
      }));
  }

  private viewItemListData = {};
  private currentSearchTab = 0;

  // digital_platform_country & digital_platform values are inherited from page_view event.
  // This value is not visible in dataLayer object. You can check it via GTag Google Analytics browser extention.
  @Listen('search:ViewItemListEvent')
  viewItemList(event: (ViewItemListEvent & CustomEvent)) {
      this.viewItemListData = {
        event: event.detail?.event,
        ecommerce: {
          currency: event.detail?.currency || '',
          value: event.detail?.value || '',
          items: event.detail?.ecommerce?.items,
        },
      };

      if (event.detail?.tabName.includes('products') || this.activeTabName.includes('products')) {
        pushToDataLayer(this.prepareEventData({ecommerce: null})); // Clear the previous ecommerce object.
        pushToDataLayer(this.prepareEventData(this.viewItemListData));
      }
  }

  @Listen('tabClicked')
  tabClicked(e: CustomEvent<TabNavbarClicked>) {
      // send event if current tab is 'Products'
      this.activeTabName = e.detail?.tab.tabName;
      this.currentSearchTab = e.detail?.num || 0;
      if (this.activeTabName.includes('products')) {
        pushToDataLayer(this.prepareEventData({ecommerce: null})); // Clear the previous ecommerce object.
        pushToDataLayer(this.prepareEventData(this.viewItemListData));
      }
  }

  @Listen('search:SearchResultClickEvent')
  exitTiSchneiderEventHandler(event: SearchResultClickEvent & CustomEvent) {
      if (
        event.detail.product === 'www.apc.com' &&
      (event.detail?.openUrl.includes('se.com') ||
        event.detail?.openUrl.includes('schneider-electric.com'))
      ) {
        const getEnv = function () {
            const hostname = window.location.hostname;
            const production = ['www.apc.com'];
            return production.indexOf(hostname) > -1 ? 'Production' : 'UAT';
        };

        pushToDataLayer(this.prepareEventData({
          event: 'exit_to_schneider',
          link_url: event.detail?.openUrl,
          digital_platform: 'apc.com',
          digital_platform_country: event.detail.country,
          environment: getEnv(),
        }));
      }
  }


  @Listen('search:identifyActiveTab')
  tabChanged(event: CustomEvent<IdentifyActiveTabEvent>) {
      this.activeTabName = event.detail?.activeTab;
  }

  @Listen('search:RedirectBannerClick')
  redirectionBannerClickedEventHandler(_event: CustomEvent<RedirectionBannerEvent>) {
      pushToDataLayer(this.prepareEventData({
        event: 'OnSiteSearch',
        category: 'banner',
        action: 'click',
        label: [
          'type: redirection-banner',
          `search-tab: ${this.activeTabName}`
        ].join(this.EVENT_LABEL_SEPARATOR)
      }));
  }

  @Listen('notification:myProductsBarCtaEvent')
  notificationBannerClickedEventHandler(event: CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'view_cart',
        ecommerce: event.detail.ecommerce,
      }));
  }

  @Listen('search:productCatalogWidgetClicked')
  globalCatalogClickedEventHandler(_event: CustomEvent<RedirectionBannerEvent>) {
      pushToDataLayer(this.prepareEventData({
        event: 'OnSiteSearch',
        category: 'banner',
        action: 'click',
        label: [
          'type: global-catalog',
          `search-tab: ${this.activeTabName}`
        ].join(this.EVENT_LABEL_SEPARATOR)
      }));
  }

  @Listen('search:productRecommendationWidgetClicked')
  productRecommendationWidgetClickedEventHandler(_event: CustomEvent<ProductRecommendationWidgetEvent>) {
      pushToDataLayer(this.prepareEventData({
        event: 'cta_click',
        cta_location: `SRP###${this.activeTabName}`,
        cta_category: 'Widget',
        cta_name: 'widget-product-recommendation',
        cta_link: _event.detail.link,
        site_search_keyword: _event.detail.term,
        site_search_label: [
          `tab:${this.activeTabName}`,
          `clickType:${_event.detail.button}`,
          `category:${_event.detail.category}`
        ].join(this.EVENT_LABEL_SEPARATOR),
        site_search_position: _event.detail.position,
      }));
  }

  @Listen('search:productRecommendationWidgetShow')
  productRecommendationWidgetShowEventHandler(_event: CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'OnSiteSearch',
        site_search_action: 'show',
        site_search_category: 'widget-product-recommendation',
        site_search_label: [
          'tab:se/products',
          'visible:true',
          _event.detail.term && `site_search_keyword:${_event.detail.term}`
        ].join(this.EVENT_LABEL_SEPARATOR)
      }));
  }


  @Listen('search:contactCustomerServiceClicked')
  contactCustomerServiceClickedEventHandler(_event: CustomEvent) {
      pushToDataLayer(_event.detail);
  }

   @Listen('search:timeForUpgradeClicked')
  timeForUpgradeClickedEventHandler(_event: CustomEvent) {
      pushToDataLayer(_event.detail);
  }

  @Listen('search:globalDecommissioningSelector', { target: 'window' })
   globalDecommissioningSelectorEventHandler(event: MouseEvent | CustomEvent) {
       pushToDataLayer(this.prepareEventData({
         event: 'OnSiteSearch',
         category: 'country-selector-widget',
         action: 'click',
         label: [
           `source:${event.detail.source}`,
           `hits:${event.detail.hits}`,
           `keyword:${event.detail.keyword}`,
           `type:${event.detail.type}`
         ].join(this.EVENT_LABEL_SEPARATOR)
       }));
   }

  @Listen('search:productNaSelector', {target: 'window'})
  productNaSelectorEventHandler(event: MouseEvent | CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'cta_click',
        cta_category: 'N/A in country',
        cta_location: event.detail.location,
        cta_name: 'country-selector',
        cta_link: event.detail?.link,
        site_search_label: [
          `tab:${event.detail.tabName}`,
          `clickType:${event.detail.clickType}`
        ].join(this.EVENT_LABEL_SEPARATOR),
        siteSearchKeyword: event.detail.keyword
      }));
  }

  @Listen('compare:compareBarAction', {target: 'window'})
  compareBarActionEventHandler(event: CustomEvent<UIaaSCompareBarEvents>) {
      pushToDataLayer(this.prepareEventData({
        event: 'OnSiteSearch',
        site_search_category: 'Compare_feature',
        action: 'click',
        siteSearchKeyword: event.detail.siteSearchKeyword,
        label: [
          `ClickedArea:${event.detail.actionType}`,
          `Card_list:${event.detail.IDs}`,
        ].join(this.EVENT_LABEL_SEPARATOR)
      }));
  }

  @Listen('compare:checkboxAction', {target: 'window'})
  compareCheckboxActionEventHandler(event: CustomEvent<UIaaSCheckboxEvents>) {
      pushToDataLayer(this.prepareEventData({
        event: 'OnSiteSearch',
        site_search_category: 'Compare_feature',
        action: 'click',
        siteSearchKeyword: event.detail.siteSearchKeyword,
        label: [
          `Tab:${event.detail?.tab ?? getSiteSearchCategory(this.activeTabName, event.detail?.appName)}`,
          `Position:${event.detail.position}`,
          'ClickedArea:checkbox',
          `Type:${event.detail.actionType}`,
          `Card:${event.detail.sku}`,
        ].join(this.EVENT_LABEL_SEPARATOR)
      }));
  }

  @Listen('conversational-search:ga-cta-banner-clicked', {target: 'window'})
  conversationalSearchCtaBannerEventHandler(event: MouseEvent | CustomEvent) {
      if (!event.detail.isGuided) {
        const clickedArea = event.detail?.clickedArea;
        const isRefinement = event.detail?.isRefinement ?? 'false';
        const labels = [`tab:${this.activeTabName}`, `isRefinement:${isRefinement}`];

        if (clickedArea) {
          const clickedAreaLabel = `clickedArea:${clickedArea}-banner`;

          labels.push(clickedAreaLabel);
        }

        pushToDataLayer(this.prepareEventData({
          event: 'genAI_search',
          search_keyword: event.detail.keyword,
          action: 'click',
          label: labels.join(this.EVENT_LABEL_SEPARATOR)
        }));
      }
  }

  @Listen('conversational-search:ga-input', {target: 'window'})
  conversationalSearchTextAreaEventHandler(event: MouseEvent | CustomEvent) {
      const isRefinent = event.detail.clickedArea === 'genAI_search_bar' ? '' : 'isRefinement:true';

      const labels = [`tab:${this.activeTabName}`, isRefinent, `clickedArea:${event.detail.clickedArea}`];

      pushToDataLayer(this.prepareEventData({
        event: event.detail.eventName,
        search_keyword: event.detail.keyword,
        site_search_action: event.detail.action,
        site_search_label: labels.join(this.EVENT_LABEL_SEPARATOR),
        site_search_category: event.detail.site_search_category || 'GenAI search',
        site_search_keyword: event.detail.keyword,
      }));
  }

  @Listen('conversational-search:ga-banner-interaction', {target: 'window'})
  conversationalSearchBannerInteraction(event: MouseEvent | CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'genAI_search',
        search_keyword: event.detail.keyword,
        site_search_action: event.detail.action
      }));
  }

  @Listen('conversational-search:ga-new-conversation', {target: 'window'})
  conversationalSearchNewOrEndOfConversationEventHandler(event: MouseEvent | CustomEvent) {

      const conversationType = event.detail?.conversationType || 'New';

      pushToDataLayer(this.prepareEventData({
        event: 'selected_conversation',
        selected_item: event.detail.item,
        conversation_type: conversationType,
        search_keyword: event.detail.keyword,
        action: 'click',
      }));
  }

  @Listen('conversational-search:show', {target: 'window'})
  conversationalSearchDynamicBannerEventHandler(event: MouseEvent | CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'OnSiteSearch',
        search_keyword: event.detail?.keyword,
        site_search_category: event.detail?.category,
        label: `tab:${this.activeTabName}`,
        action: 'show',
      }));
  }

  @Listen('conversational-search:ga-icon-action', {target: 'window'})
  conversationalSearchIconActionEventHandler(event: MouseEvent | CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'icon_click',
        icon_name: event.detail.actionItem,
        search_keyword: event.detail.keyword,
        site_search_action: event.detail?.actionType
      }));
  }

  @Listen('conversational-search:ga-cta', {target: 'window'})
  conversationalSearchCtaEventHandler(event: MouseEvent | CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'cta_click',
        cta_location: event.detail.ctaLocation ?? 'genAI_search',
        cta_name: event.detail.ctaName,
        cta_link: event.detail?.ctaLink,
        cta_category: event.detail?.ctaCategory,
        site_search_keyword: event.detail.keyword,
      }));
  }

  @Listen('conversational-search:ga-suggestion', {target: 'window'})
  conversationalSearchSuggestionEventHandler(event: MouseEvent | CustomEvent) {
      const clickTypeLabel = event.detail.clickType && `clickType:${event.detail.clickType}`;
      const indexLabel = event.detail.index?.toString() && `index:${event.detail.index}`;
      const tabNameLabel = this.activeTabName && `tabName:${this.activeTabName}`;

      pushToDataLayer(this.prepareEventData({
        event: 'select_suggestion',
        suggestion_name: event.detail.suggestionName,
        suggestion_category: event.detail.category,
        destination_url: event.detail?.url,
        search_keyword: event.detail.keyword,
        label: [
          tabNameLabel,
          clickTypeLabel,
          indexLabel
        ].filter(el => el !== undefined).join(this.EVENT_LABEL_SEPARATOR)
      }));
  }

  @Listen('conversational-search:ga-result-suggestions', {target: 'window'})
  conversationalSearchResultSuggestionsEventHandler(event: MouseEvent | CustomEvent) {
      pushToDataLayer(this.prepareEventData({'ecommerce': null})); // Clear previous e-commerce object, requested by DA team
      pushToDataLayer(this.prepareEventData({
        event: 'view_item_list',
        item_list_name: undefined,
        item_location: 'genAI_search',
        ecommerce: {
          items: event.detail?.suggestions.map((suggestion, index) => {
              return {
                index,
                item_category: 'genAI_search',
                item_id: suggestion?.productId,
                item_name: suggestion?.title,
                price: undefined,
                quantity: undefined
              };
          })
        }
      }));
  }

  @Listen('conversational-search:ga-suggestion-select', {target: 'window'})
  conversationalSearchSuggestionSelectEventHandler(event: MouseEvent | CustomEvent) {
      pushToDataLayer(this.prepareEventData({'ecommerce': null})); // Clear previous e-commerce object, requested by DA team
      pushToDataLayer(this.prepareEventData({
        event: 'select_item',
        item_list_name: undefined,
        item_location: 'genAI_search',
        ecommerce: {
          items: {
            index: event.detail?.index,
            item_category: 'genAI_search',
            item_id: event.detail?.suggestion?.productId,
            item_name: event.detail?.suggestion?.title,
            price: undefined,
            quantity: undefined
          }
        }
      }));
  }

  @Listen('sidePanel:SearchResultClickEvent')
  sidePanelResultClickEventHandler(event: SearchResultClickEvent & CustomEvent) {
      if (
        SidePanelActionType.SidePanelOpen === event.detail.actionType
      || SidePanelActionType.SidePanelClose === event.detail.actionType
      ) {
        return;
      }

      const {
        searchPagePosition,
        searchResultPosition,
        site_search_category,
        siteSearchKeyword,
        openUrl,
      } = event.detail;

      pushToDataLayer(this.prepareEventData({
        event: 'searchResultClick',
        searchPagePosition,
        searchResultPosition,
        site_search_category,
        siteSearchKeyword,
        destination_URL: openUrl,
      }));
  }

    @Listen('search:RbcSearchResultClickEvent')
  rbcResultClickEventHandler(event: SearchResultClickEvent & CustomEvent) {
      const {
        searchPagePosition,
        siteSearchCategory,
        searchResultPosition,
        siteSearchKeyword,
        siteSearchLabel,
        destination_URL,
        item_id,
        item_name,
        quantity
      } = event.detail;

      pushToDataLayer(this.prepareEventData({
        event: 'searchResultClick',
        searchPagePosition,
        searchResultPosition,
        siteSearchCategory,
        siteSearchKeyword,
        siteSearchLabel,
        destination_URL : destination_URL,
        search_location: 'RBC Selector'
      }));

      pushToDataLayer(this.prepareEventData({
        event: 'select_item',
        ecommerce: {
          item_list_name: 'RBC Selector',
          items: {
            index: searchResultPosition,
            item_category: siteSearchCategory,
            item_id: item_id,
            item_name: item_name,
            price: undefined,
            quantity: quantity
          }
        }
      }));
  }

  @Listen('sidePanel:attachmentClicked')
    onSidePanelAttachmentClicked(e: CustomEvent) {
        pushToDataLayer(this.prepareEventData({
          event: 'searchResultClick',
          searchPagePosition: e.detail.searchPagePosition,
          site_search_category: e.detail.tabName,
          siteSearchKeyword: e.detail.siteSearchKeyword,
          destination_URL: e.detail.openUrl,
          label: [
            `clickedArea:${e.detail.clickedArea}`,
            `tabName:${e.detail.tabName}`,
            `position:${e.detail.searchPagePosition}`,
            `clickType:${e.detail.clickType}`,
            `actionType:${e.detail.actionType}`,
            `commStatus:${e.detail.commStatus}`
          ].join(this.EVENT_LABEL_SEPARATOR)
        }));
    }

  @Listen('additional-conditions:ga-cta-banner-clicked', {target: 'window'})
  additionalConditionsCtaBannerEventHandler(event: MouseEvent | CustomEvent<any>) {//eslint-disable-line
      pushToDataLayer({
        event: 'cta_click',
        cta_location: `Search Result Page_${this.activeTabName}`,
        cta_name: event.detail.name,
        cta_link: event.detail.ctaLink,
        cta_category: event.detail.category,
        site_search_keyword: event.detail.keyword,
      });

  }

  @Listen('additional-conditions:ga-banner-appearance', {target: 'window'})
  additionalConditionsBannerAppearanceEventHandler(event: MouseEvent | CustomEvent<any>) {//eslint-disable-line
      if ((this.activeTabName == 'se/all' && event.detail.isProductTab == false) || this.activeTabName == 'se/products') {
        pushToDataLayer({
          event: 'OnSiteSearch',
          site_search_category: event.detail.category,
          action: 'show',
          label: [
            `tab:${this.activeTabName}`,
          ].join('###'),
          site_search_keyword: event.detail.keyword
        });
      }
  }

  @Listen('substitution-tool:ga-cta-banner-clicked', {target: 'window'})
  substitutionToolCtaBannerEventHandler(event: MouseEvent | CustomEvent<any>) {//eslint-disable-line
      pushToDataLayer({
        event: 'cta_click',
        cta_location: 'Search Result Page',
        cta_name: 'substitution-tool',
        cta_link: event.detail.ctaLink,
        site_search_keyword: event.detail.keyword,
        site_search_label: [
          `tab:${this.activeTabName}`,
          `clickType:${event.detail.clickType}`,
          `redirectionUrl:${event.detail.ctaLink}`
        ].join('###'),
      });
  }

  /*
  For Mobile Filtering only
 */
  @Listen('search:mobileFilteringButtonResultsClick')
  onMobileFilteringButtonResultsClick(e: CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'filterClick',
        filterAction: MobileFilteringActionType.FilterSelected,
        filterCategory: MobileFilteringCategoryType.ShowResults,
        filterName: null,
      }));
  }

  @Listen('search:mobileFilteringResetButtonClick')
  onMobileFilteringResetButtonClick(e: CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'filterClick',
        filterAction: MobileFilteringActionType.FilterDeselected,
        filterCategory: MobileFilteringCategoryType.ResetFilters,
        filterName: null,
      }));
  }

  @Listen('search:mobileFilteringFacetClickEvent')
  facetMobileFilteringClickEventHandler(e: FacetChangedEvent & CustomEvent) {
      const {tabName, filterAction, filterCategory, filterName, event, filterCount, filterSearchInputValue} = e.detail;
      const details: FacetChangedEvent = {event, filterAction, filterCategory, filterName, filterCount, filterSearchInputValue};
      details.site_search_label = [
        ['tabName', tabName],
        ['filterSearchInput', filterSearchInputValue || null],
        ['clickedArea', 'show_results']].filter(el => el[1] !== null).map(el => el.join(':')).join(this.EVENT_LABEL_SEPARATOR);
      pushToDataLayer(this.prepareEventData(details));
  }

  @Listen('search:mobileFilteringChipClick')
  filterMobileFilteringChipClickHandler(e: FacetChangedEvent & CustomEvent) {
      const {event, filterName, filterCategory, filterAction, tabName} = e.detail;
      const details: FacetChangedEvent = {event, filterName, filterCategory, filterAction};
      details.site_search_label = [
        ['tabName', tabName],
        ['clickedArea', 'chips']].filter(el => el[1] !== null).map(el => el.join(':')).join(this.EVENT_LABEL_SEPARATOR);
      pushToDataLayer(this.prepareEventData(details));
  }

  /**
   New Facest Logic
   */
  @Listen('facest:chipRemoveOnTab')
  handleChipRemove(event: CustomEvent) {
      const parsedUniqueId = event.detail.uniqueId.split(FILTERS_ID_SEPARATOR);
      const eventData = {
        event: 'filterClick',
        filterAction: 'Filter deselected',
        filterCategory: event.detail?.facetCategoryDisplayName,
        filterName: decodeURIComponent(parsedUniqueId[parsedUniqueId.length - 1]),
        filter_count: event.detail.count,
        site_search_label: `tabName:${event.detail.tabName}${EventLabelSeparatorConstant}clickedArea:chips`
      };
      pushToDataLayer(this.prepareEventData(eventData));
  }

  @Listen('facest:onFilterChange')
  handleOnFilterChange(event: CustomEvent) {
      const parsedUniqueId = event.detail.uniqueId.split(FILTERS_ID_SEPARATOR);
      const eventData = {
        event: 'filterClick',
        filterAction: event.detail.selected ? 'Filter selected' : 'Filter deselected',
        filterCategory: event.detail?.facetCategoryDisplayName,
        filterName: decodeURIComponent(parsedUniqueId[parsedUniqueId.length - 1]),
        filter_count: event.detail.count,
        site_search_label: `tabName:${event.detail.tabName}${EventLabelSeparatorConstant}clickedArea:checkbox`
      };
      pushToDataLayer(this.prepareEventData(eventData));
  }

  @Listen('facest:onResetAll')
  handleOnResetAll(event: CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'filterClick',
        site_search_label: `tabName:${event.detail.tabName}${EventLabelSeparatorConstant}clickedArea:resetAll`
      }));
  }

  @Listen('facest:onResetFilterDeselect')
  handleOnResetFilterDeselect(event: CustomEvent) {
      const label = `tabName:${event.detail.tabName}${EventLabelSeparatorConstant}clickedArea:${event.detail.clickedArea}`;
      pushToDataLayer(this.prepareEventData({...event.detail, site_search_label: label}));
  }

  @Listen('facest:onInsearchInput')
  handleOnInSearchInput(event: CustomEvent) {
      const {
        category,
        action,
        clickedArea,
        eventName,
        siteSearchKeyword,
        tabName,
        facetCategory
      } = event.detail;

      const label = `clickedArea:insearch${EventLabelSeparatorConstant}tabName:${tabName}${EventLabelSeparatorConstant}category:${facetCategory}`;

      pushToDataLayer(this.prepareEventData({
        category,
        action,
        clickedArea,
        event: eventName,
        siteSearchKeyword,
        tabName,
        facetCategory,
        site_search_label: label
      }));
  }

  @Listen('search:dummyWidgetEvent', {target: 'window'})
  dummyWidgetEventHandler(event: MouseEvent | CustomEvent) {
      pushToDataLayer(this.prepareEventData({
        event: 'cta_click',
        cta_location: event.detail.location,
        cta_name: event.detail.ctaName,
        cta_link: event.detail?.ctaLink,
        cta_category: event.detail.category,
        site_search_keyword: event.detail.keyword,
        site_search_label: event.detail.label
      }));
  }

  @Listen('search:b2cRedirectionEvent', {target: 'document'})
  b2cRedirectionBannerEventHandler(event: MouseEvent | CustomEvent) {
      pushToDataLayer({
        event: 'cta_click',
        cta_location: event.detail.location,
        cta_name: 'Banner',
        cta_link: event.detail.ctaLink,
        cta_category: 'Redirection button',
        site_search_keyword: event.detail.keyword,
        site_search_label: [
          `tab:${this.activeTabName}`,
          `position:${event.detail.position}`
        ].join('###'),
      });
  }

  @Listen('search:FilterViewButtonClick', {target: 'window'})
  filterViewButtonClickEventHandler(event: MouseEvent | CustomEvent) {
      pushToDataLayer({
        event: 'cta_click',
        cta_location: 'Search Result Page ',
        cta_name: event.detail.name,
        cta_link: event.detail.link,
        cta_category: event.detail.category,
        site_search_keyword: event.detail.keyword,
        site_search_label: `tab:${this.activeTabName}`
      });
  }

  @Listen('search:ctaWidgetv2Event')
  ctaWidgetv2EventHandler({detail}: CustomEvent<CtaWidgetClickEvent>) {
      pushToDataLayer(this.prepareEventData({
        event: 'cta_click',
        cta_location: 'SRP',
        cta_category: 'Widget link',
        cta_name: `${detail.category}`,
        cta_link: `${detail.redirectionUrl}`,
        site_search_keyword: detail.term,
        site_search_label: [
          `tab:${this.activeTabName}`,
          'clickType:right',
        ].join(this.EVENT_LABEL_SEPARATOR),
      }));
  }

  @Listen('search:ctaCircularWidget', {target: 'window'})
  ctaCircularFooterWidgetEventHandler({detail}: CustomEvent<CtaWidgetClickEvent>) {
      pushToDataLayer(this.prepareEventData({
        event: 'cta_click',
        cta_location: `${detail.ctaLocation}`,
        cta_name: `${detail.ctaName}`,
        cta_link: `${detail.redirectionUrl}`,
        cta_category: `${detail.category}`,
      }));
  }

  @Listen('search:ItemClicked')
  itemClickedEventHandler({detail}: CustomEvent<ItemClickedEvent>) {
      pushToDataLayer(this.prepareEventData({
        event: 'item_clicked',
        selectedOption: detail.selectedOption,
        selectedSubOption: detail.selectedSubOption || '',
        selectedLocation: detail.selectedLocation || this.digitalPlatform
      }));
  }

  @Listen('search:CircularRangeCtaClick')
  ctaCircularRangeEventHandler({detail}: CustomEvent<CtaClickEvent>) {
      pushToDataLayer(this.prepareEventData({
        event: 'cta_click',
        cta_location: detail.ctaLocation,
        cta_name: detail.ctaName,
        cta_link: detail.ctaLink,
        cta_category: detail.ctaCategory,
      }));
  }

  /**
   New Facest Logic
   */

  render() {
      return <slot/>;
  }
}
